import type { ReactElement } from 'react';
import React, { useEffect } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { getHashFromURL } from '@noths/polaris-client-utils';

import * as styles from './Accordion.styles';
import { IconChevronUp } from 'client-ribbons-design-system/components/foundations/Iconography/IconChevronUp';
import type { ThemeColorOrCSSVar } from 'client-ribbons-design-system/types/color';

interface AccordionProps {
  children: ReactElement | string;
  iconColor?: 'currentColor' | ThemeColorOrCSSVar;
  id: string;
  onClick?: (ev: React.MouseEvent<HTMLDetailsElement, MouseEvent>) => void;
  onToggle?: ({ open }: { open: boolean }) => void;
  onVisible?: () => void;
  open?: boolean;
  title: ReactElement | string;
}

export const Accordion = ({
  children,
  iconColor = 'currentColor',
  id,
  onClick,
  onToggle,
  onVisible,
  open = true,
  title,
}: AccordionProps) => {
  const detailsRef = useImpressionRef<HTMLDetailsElement>(() => onVisible?.());

  const setOpen = (value: boolean) => {
    if (detailsRef.current) {
      detailsRef.current.setAttribute('open', String(value));
    }
  };

  const handleToggle = () => {
    if (detailsRef.current) {
      onToggle?.({ open: detailsRef.current.open });
    }
  };

  const handleHashChange = ({ newURL }: HashChangeEvent) => {
    if (getHashFromURL(newURL) === id) {
      setOpen(true);
    }
  };

  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);

    if (getHashFromURL(window.location.href) === id) {
      setOpen(true);
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return (
    <details css={styles.details} id={id} onToggle={handleToggle} open={open} ref={detailsRef}>
      <summary onClick={onClick}>
        <div>{title}</div>
        <IconChevronUp color={iconColor} height={16} width={16} />
      </summary>
      {children}
    </details>
  );
};
